import type { InboxFilters } from '../features/inbox/use-inbox.ts';
import type { MerchantListFilters } from '../features/merchants/use-merchants.ts';
import type { SuppressionFilters } from '../features/compliance/use-suppression.ts';

/** TanStack Query keys. Every query in the app takes its key from here. */
export const queryKeys = {
  workspace: (workspaceId: string) => ['workspace', workspaceId] as const,
  members: (workspaceId: string) => ['members', workspaceId] as const,
  profile: (userId: string) => ['profile', userId] as const,
  invitation: (workspaceId: string, userId: string) => ['invitation', workspaceId, userId] as const,
  platformWorkspaces: () => ['platform', 'workspaces'] as const,
  /** Prefix of every merchant query of a workspace (invalidate after any merchant change). */
  merchants: (workspaceId: string) => ['merchants', workspaceId] as const,
  merchantList: (workspaceId: string, filters: MerchantListFilters) =>
    ['merchants', workspaceId, 'list', filters] as const,
  merchant: (workspaceId: string, merchantId: string) =>
    ['merchants', workspaceId, 'detail', merchantId] as const,
  merchantTimeline: (workspaceId: string, merchantId: string) =>
    ['merchants', workspaceId, 'timeline', merchantId] as const,
  fieldDefinitions: (workspaceId: string) => ['field-definitions', workspaceId] as const,
  tags: (workspaceId: string) => ['tags', workspaceId] as const,
  numbers: (workspaceId: string) => ['numbers', workspaceId] as const,
  /** Prefix of every inbox query of a workspace (live updates invalidate it). */
  inbox: (workspaceId: string) => ['inbox', workspaceId] as const,
  inboxList: (workspaceId: string, filters: InboxFilters) =>
    ['inbox', workspaceId, 'list', filters] as const,
  conversation: (workspaceId: string, merchantId: string) =>
    ['inbox', workspaceId, 'conversation', merchantId] as const,
  messageFooter: (workspaceId: string, merchantId: string) =>
    ['inbox', workspaceId, 'footer', merchantId] as const,
  /** The bell: the signed-in user's own notifications (RLS returns no one else's). */
  notifications: (workspaceId: string) => ['notifications', workspaceId] as const,
  /** Suppression list (admins). Prefix of every list query. */
  suppression: (workspaceId: string) => ['suppression', workspaceId] as const,
  suppressionList: (workspaceId: string, filters: SuppressionFilters) =>
    ['suppression', workspaceId, 'list', filters] as const,
  merchantSuppression: (workspaceId: string, merchantId: string) =>
    ['suppression', workspaceId, 'merchant', merchantId] as const,
  consent: (workspaceId: string, merchantId: string) =>
    ['merchants', workspaceId, 'consent', merchantId] as const,
  /** M7: the platform's state rules, holidays and area codes (the same for every workspace). */
  complianceReference: () => ['compliance-reference'] as const,
  /** M7: the workspace's stricter-only overrides. */
  stateOverrides: (workspaceId: string) => ['state-overrides', workspaceId] as const,
  /** M8: CSV imports (admins). Prefix of every import query. */
  imports: (workspaceId: string) => ['imports', workspaceId] as const,
  importDetail: (workspaceId: string, importId: string) =>
    ['imports', workspaceId, 'detail', importId] as const,
  importPreview: (workspaceId: string, importId: string) =>
    ['imports', workspaceId, 'preview', importId] as const,
  importIssues: (workspaceId: string, importId: string) =>
    ['imports', workspaceId, 'issues', importId] as const,
  /** M8: lead sources (webhook and saved vendor mappings) and their recent leads. */
  intakeSources: (workspaceId: string) => ['intake-sources', workspaceId] as const,
  intakeEvents: (workspaceId: string, sourceId: string) =>
    ['intake-sources', workspaceId, 'events', sourceId] as const,
  /** M9: campaigns (admins). Prefix of every campaign query. */
  campaigns: (workspaceId: string) => ['campaigns', workspaceId] as const,
  campaign: (workspaceId: string, campaignId: string) =>
    ['campaigns', workspaceId, 'detail', campaignId] as const,
  campaignPreview: (workspaceId: string, campaignId: string) =>
    ['campaigns', workspaceId, 'preview', campaignId] as const,
  campaignStats: (workspaceId: string, campaignId: string) =>
    ['campaigns', workspaceId, 'stats', campaignId] as const,
  campaignRecipients: (
    workspaceId: string,
    campaignId: string,
    filter: { status: string; page: number },
  ) => ['campaigns', workspaceId, 'recipients', campaignId, filter] as const,
  /** Campaign names for thread labels (every member). */
  campaignLabels: (workspaceId: string, ids: readonly string[]) =>
    ['campaign-labels', workspaceId, ids] as const,
  merchantSources: (workspaceId: string) => ['merchant-sources', workspaceId] as const,
};
